/**
 * Marmitek Cookie Consent Banner.
 *
 * Compact bottom-left banner + centered modal for granular preferences.
 * Monochrome (black / white / grey) styling. Wires Shopify Customer
 * Privacy API and Google Consent Mode v2.
 */

const CONSENT_CATEGORIES = ['necessary', 'marketing', 'analytics', 'preferences'];
const STORAGE_KEY = 'marmitek_consent_v1';

// Static cookie inventory mirrors snippets/cookie-declaration.liquid so
// users can inspect what falls under each consent category without leaving
// the modal. Purposes are translated via L10N.declaration.cookie_<purpose_key>;
// expiry is rendered with translated unit suffixes.
const COOKIES_DATA = {
  necessary: [
    { name: 'cart',                provider: 'Shopify',           purpose_key: 'cookie_cart',             expiry: '2w' },
    { name: '_shopify_y',          provider: 'Shopify',           purpose_key: 'cookie_shopify_y',        expiry: '1y' },
    { name: '_shopify_s',          provider: 'Shopify',           purpose_key: 'cookie_shopify_s',        expiry: '30min' },
    { name: 'secure_customer_sig', provider: 'Shopify',           purpose_key: 'cookie_secure_sig',       expiry: '1y' },
    { name: 'cart_currency',       provider: 'Shopify',           purpose_key: 'cookie_cart_currency',    expiry: 'session' },
    { name: 'marmitek_consent_v1', provider: 'Marmitek',          purpose_key: 'cookie_marmitek_consent', expiry: '1y' },
  ],
  analytics: [
    { name: '_ga',     provider: 'Google Analytics',   purpose_key: 'cookie_ga',           expiry: '2y' },
    { name: '_ga_*',   provider: 'Google Analytics',   purpose_key: 'cookie_ga_container', expiry: '2y' },
    { name: '_gid',    provider: 'Google Analytics',   purpose_key: 'cookie_gid',          expiry: '24h' },
    { name: '_clck',   provider: 'Microsoft Clarity',  purpose_key: 'cookie_clck',         expiry: '1y' },
    { name: '_clsk',   provider: 'Microsoft Clarity',  purpose_key: 'cookie_clsk',         expiry: '1d' },
  ],
  marketing: [
    { name: '_gcl_au',                            provider: 'Google Ads',         purpose_key: 'cookie_gcl_au',  expiry: '90d' },
    { name: '_gcl_aw',                            provider: 'Google Ads',         purpose_key: 'cookie_gcl_aw',  expiry: '90d' },
    { name: 'tdclid_sn',                          provider: 'Tradedoubler',       purpose_key: 'cookie_tdclid',  expiry: '1y' },
    { name: '_fbp',                               provider: 'Meta (Facebook)',    purpose_key: 'cookie_fbp',     expiry: '90d' },
    { name: 'IDE / NID',                          provider: 'Google DoubleClick', purpose_key: 'cookie_ide',     expiry: '13mo' },
    { name: 'bcookie / lidc / UserMatchHistory',  provider: 'LinkedIn',           purpose_key: 'cookie_linkedin', expiry: '1y' },
    { name: '__kla_id',                           provider: 'Klaviyo',            purpose_key: 'cookie_kla',     expiry: '2y' },
  ],
  preferences: [
    { name: 'localization',   provider: 'Shopify', purpose_key: 'cookie_localization',   expiry: '1y' },
    { name: '_orig_referrer', provider: 'Shopify', purpose_key: 'cookie_orig_referrer',  expiry: 'session' },
  ],
};

const formatExpiry = (raw, decl) => {
  if (raw === 'session') return decl.session || 'Session';
  const m = String(raw).match(/^(\d+)(min|mo|h|d|w|y)$/);
  if (!m) return raw;
  const [, n, unit] = m;
  const num = Number(n);
  const map = {
    min: decl.minutes,
    h: decl.hours,
    d: num === 1 ? (decl.day || decl.days) : decl.days,
    w: decl.weeks,
    mo: decl.months,
    y: num === 1 ? (decl.year || decl.years) : decl.years,
  };
  return `${num} ${map[unit] || unit}`;
};

const STYLES = `
  :host {
    --cb-bg: #FFFFFF;
    --cb-bg-soft: #F5F5F5;
    --cb-text: #0A0A0A;
    --cb-text-muted: #6B6B6B;
    --cb-border: rgba(0, 0, 0, 0.10);
    --cb-border-strong: rgba(0, 0, 0, 0.20);
    --cb-toggle-off: #D4D4D4;
    --cb-radius: 14px;
    --cb-radius-modal: 16px;
    --cb-radius-btn: 999px;
    --cb-shadow:
      0 12px 28px -10px rgba(0, 0, 0, 0.18),
      0 4px 10px -4px rgba(0, 0, 0, 0.10);
    --cb-shadow-modal:
      0 28px 60px -14px rgba(0, 0, 0, 0.32),
      0 10px 22px -10px rgba(0, 0, 0, 0.18);
    --cb-font: 'Poppins', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    position: fixed;
    inset: 0;
    pointer-events: none;
    /* Max int32 z-index so the banner always wins against any third-party
       rule on product / store-locator pages. */
    z-index: 2147483647;
    font-family: var(--cb-font);
    color: var(--cb-text);
    /* Isolate so descendant z-indexes never escape and so external CSS
       cannot accidentally pull the banner into another stacking context. */
    isolation: isolate;
    contain: layout style;
  }
  :host([hidden]) { display: none; }

  .banner, .banner *, .modal, .modal * {
    font-family: var(--cb-font);
    font-feature-settings: 'kern' 1, 'liga' 1, 'ss01' 1;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    box-sizing: border-box;
  }

  /* COMPACT BANNER ------------------------------------------------------ */
  .banner {
    position: absolute;
    bottom: 20px;
    left: 20px;
    width: min(calc(100vw - 40px), 440px);
    background: var(--cb-bg);
    border: 1px solid var(--cb-border);
    border-radius: var(--cb-radius);
    box-shadow: var(--cb-shadow);
    pointer-events: auto !important;
    padding: 16px 18px 14px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    transform: translateY(120%);
    opacity: 0;
    will-change: transform, opacity;
  }
  .banner *, .modal[data-visible="true"] * { pointer-events: auto; }
  .banner-text {
    margin: 0;
    font-size: 13px;
    line-height: 1.55;
    color: var(--cb-text-muted);
    letter-spacing: -0.005em;
  }
  .banner-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    flex-wrap: wrap;
  }
  .banner-actions-right {
    display: flex;
    gap: 8px;
    flex: 0 0 auto;
  }

  /* BUTTONS ------------------------------------------------------------- */
  .btn {
    height: 36px;
    padding: 0 18px;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: -0.005em;
    border-radius: var(--cb-radius-btn);
    border: 1px solid var(--cb-border-strong);
    background: var(--cb-bg);
    color: var(--cb-text);
    cursor: pointer;
    font-family: inherit;
    white-space: nowrap;
    transition:
      background 160ms ease,
      color 160ms ease,
      border-color 160ms ease,
      transform 100ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .btn:hover { background: var(--cb-bg-soft); }
  .btn:active { transform: scale(0.98); }
  .btn:focus-visible {
    outline: 2px solid var(--cb-text);
    outline-offset: 2px;
  }
  .btn--primary {
    background: var(--cb-text);
    color: #FFFFFF;
    border-color: var(--cb-text);
  }
  .btn--primary:hover { background: #2A2A2A; }
  .btn--ghost {
    background: transparent;
    border-color: transparent;
    padding: 0 14px;
    color: var(--cb-text-muted);
  }
  .btn--ghost:hover {
    color: var(--cb-text);
    background: transparent;
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  /* OVERLAY + MODAL ----------------------------------------------------- */
  .overlay {
    position: absolute;
    inset: 0;
    background: rgba(20, 20, 20, 0.45);
    pointer-events: none;
    opacity: 0;
    transition: opacity 240ms ease;
  }
  .overlay[data-visible="true"] {
    pointer-events: auto !important;
    opacity: 1;
  }

  .modal {
    position: absolute;
    top: 50%;
    left: 50%;
    width: min(calc(100vw - 32px), 560px);
    max-height: calc(100vh - 48px);
    background: var(--cb-bg);
    border-radius: var(--cb-radius-modal);
    box-shadow: var(--cb-shadow-modal);
    pointer-events: none;
    opacity: 0;
    transform: translate(-50%, -48%) scale(0.96);
    transition: opacity 240ms ease, transform 280ms cubic-bezier(0.22, 1, 0.36, 1);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .modal[data-visible="true"] {
    pointer-events: auto !important;
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }

  .modal-scroll {
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    scrollbar-color: var(--cb-border-strong) transparent;
  }
  .modal-scroll::-webkit-scrollbar { width: 6px; }
  .modal-scroll::-webkit-scrollbar-thumb { background: var(--cb-border-strong); border-radius: 3px; }

  .modal-header { padding: 24px 26px 4px; }
  .modal-title {
    margin: 0 0 6px;
    font-size: 18px;
    font-weight: 600;
    letter-spacing: -0.012em;
    color: var(--cb-text);
  }
  .modal-desc {
    margin: 0;
    font-size: 13px;
    line-height: 1.6;
    color: var(--cb-text-muted);
  }

  .modal-list {
    margin: 18px 26px 0;
    border: 1px solid var(--cb-border);
    border-radius: 12px;
    overflow: hidden;
    background: var(--cb-bg);
  }
  .category-block { border-bottom: 1px solid var(--cb-border); }
  .category-block:last-child { border-bottom: none; }
  .category-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    padding: 14px 18px;
  }
  .category-info {
    display: flex;
    align-items: baseline;
    gap: 10px;
    min-width: 0;
    flex: 1;
  }
  .category-name {
    font-size: 14px;
    font-weight: 500;
    color: var(--cb-text);
    letter-spacing: -0.005em;
  }
  .category-meta-btn {
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    font: inherit;
    color: var(--cb-text-muted);
    font-size: 12px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    letter-spacing: -0.005em;
    transition: color 160ms ease;
  }
  .category-meta-btn:hover { color: var(--cb-text); }
  .category-meta-btn svg {
    width: 12px;
    height: 12px;
    transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .category-meta-btn[aria-expanded="true"] svg { transform: rotate(180deg); }
  .category-actions {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }
  .always-on {
    font-size: 12px;
    font-weight: 500;
    color: var(--cb-text-muted);
    letter-spacing: -0.005em;
  }
  .cookie-list {
    overflow: hidden;
    height: 0;
    will-change: height;
    background: var(--cb-bg-soft);
  }
  .cookie-list-inner {
    padding: 4px 18px 14px;
  }
  .cookie-item {
    padding: 10px 0;
    border-top: 1px solid var(--cb-border);
  }
  .cookie-item:first-child { border-top: none; }
  .cookie-name {
    font-size: 12.5px;
    font-weight: 500;
    color: var(--cb-text);
    word-break: break-word;
  }
  .cookie-meta {
    margin-top: 2px;
    font-size: 11.5px;
    color: var(--cb-text-muted);
    letter-spacing: -0.005em;
  }
  .cookie-meta-sep { margin: 0 6px; opacity: 0.5; }
  .cookie-purpose {
    margin-top: 4px;
    font-size: 12px;
    line-height: 1.5;
    color: var(--cb-text-muted);
  }

  .switch {
    appearance: none;
    -webkit-appearance: none;
    width: 38px;
    height: 22px;
    border-radius: 999px;
    background: var(--cb-toggle-off);
    border: none;
    cursor: pointer;
    position: relative;
    transition: background 200ms ease;
    flex-shrink: 0;
    padding: 0;
  }
  .switch::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 18px;
    height: 18px;
    background: #FFFFFF;
    border-radius: 50%;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
    transition: left 220ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .switch[aria-checked="true"] { background: var(--cb-text); }
  .switch[aria-checked="true"]::after { left: 18px; }
  .switch:focus-visible {
    outline: 2px solid var(--cb-text);
    outline-offset: 2px;
  }

  .modal-privacy {
    margin: 16px 26px 0;
    padding: 0;
    font-size: 12px;
    line-height: 1.55;
    color: var(--cb-text-muted);
  }
  .modal-privacy a {
    color: var(--cb-text);
    text-decoration: underline;
    text-underline-offset: 2px;
    transition: opacity 160ms ease;
  }
  .modal-privacy a:hover { opacity: 0.7; }

  .modal-footer {
    margin-top: 18px;
    padding: 16px 26px;
    border-top: 1px solid var(--cb-border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    flex-wrap: wrap;
  }
  .footer-left { display: flex; gap: 8px; }

  /* RESPONSIVE / REDUCED MOTION / PRINT --------------------------------- */
  @media (max-width: 520px) {
    .banner {
      left: 8px;
      right: 8px;
      bottom: 8px;
      width: auto;
      padding: 16px 16px 14px;
    }
    .banner-actions { flex-wrap: wrap; gap: 6px; }
    .banner-actions-right { width: 100%; }
    .banner-actions-right .btn { flex: 1; }
    .modal {
      width: calc(100vw - 16px);
      max-height: calc(100vh - 24px);
    }
    .modal-header { padding: 20px 18px 4px; }
    .modal-list { margin: 14px 18px 0; }
    .modal-privacy { margin: 14px 18px 0; }
    .modal-footer {
      padding: 14px 18px;
      flex-direction: column-reverse;
      align-items: stretch;
    }
    .footer-left { width: 100%; }
    .footer-left .btn { flex: 1; }
    .modal-footer .btn--primary { width: 100%; }
  }

  @media (prefers-reduced-motion: reduce) {
    .banner, .modal, .overlay, .switch, .switch::after, .btn { transition: none !important; }
  }

  @media print {
    :host { display: none !important; }
  }
`;

const TEMPLATE = (l10n) => `
  <div class="banner" role="region" aria-label="${l10n.title || 'Cookies'}">
    <p class="banner-text">${l10n.description || ''}</p>
    <div class="banner-actions">
      <button class="btn btn--ghost" data-action="customize" type="button">${l10n.customize || 'Consent Settings'}</button>
      <div class="banner-actions-right">
        <button class="btn" data-action="reject-all" type="button">${l10n.reject_all || 'Deny'}</button>
        <button class="btn btn--primary" data-action="accept-all" type="button">${l10n.accept_all || 'Accept all'}</button>
      </div>
    </div>
  </div>

  <div class="overlay" data-overlay></div>

  <div class="modal" role="dialog" aria-modal="true" aria-labelledby="cb-modal-title">
    <div class="modal-scroll">
      <div class="modal-header">
        <h2 class="modal-title" id="cb-modal-title">${l10n.modal_title || 'Your Privacy'}</h2>
        <p class="modal-desc">${l10n.description || ''}</p>
      </div>
      <div class="modal-list" data-modal-list></div>
      <p class="modal-privacy">
        ${
          l10n.privacy_url
            ? `${l10n.privacy_intro || 'Read how we handle your data in our'} <a href="${l10n.privacy_url}">${l10n.privacy_link || 'Privacy Policy'}</a>.`
            : ''
        }
      </p>
    </div>
    <div class="modal-footer">
      <div class="footer-left">
        <button class="btn" data-action="reject-all" type="button">${l10n.reject_all || 'Deny'}</button>
        <button class="btn" data-action="accept-all" type="button">${l10n.accept_all || 'Accept all'}</button>
      </div>
      <button class="btn btn--primary" data-action="save" type="button">${l10n.save_preferences || 'Save'}</button>
    </div>
  </div>
`;

const CHEVRON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>`;

const COOKIE_ITEM = (cookie, decl, expiryStr) => `
  <div class="cookie-item">
    <div class="cookie-name">${cookie.name}</div>
    <div class="cookie-meta">${cookie.provider}<span class="cookie-meta-sep">·</span>${expiryStr}</div>
    <div class="cookie-purpose">${(decl && decl[cookie.purpose_key]) || ''}</div>
  </div>
`;

const CATEGORY_ROW = (cat, l10n) => {
  const decl = l10n.declaration || {};
  const cookies = COOKIES_DATA[cat.id] || [];
  const count = cookies.length;
  const cookiesLabel = decl.cookies_label || 'cookies';
  const items = cookies.map((c) => COOKIE_ITEM(c, decl, formatExpiry(c.expiry, decl))).join('');
  return `
  <div class="category-block">
    <div class="category-row">
      <div class="category-info">
        <span class="category-name">${l10n[`cat_${cat.id}_name`] || cat.id}</span>
        ${
          count > 0
            ? `<button class="category-meta-btn" type="button" data-expand="${cat.id}" aria-expanded="false" aria-controls="cb-cookies-${cat.id}">${count} ${cookiesLabel}${CHEVRON}</button>`
            : ''
        }
      </div>
      <div class="category-actions">
        ${
          cat.required
            ? `<span class="always-on">${l10n.always_on || l10n.required || 'On'}</span>`
            : `<button
                 class="switch"
                 role="switch"
                 aria-checked="${cat.checked ? 'true' : 'false'}"
                 aria-label="${l10n[`cat_${cat.id}_name`] || cat.id}"
                 data-toggle="${cat.id}"
                 type="button"
               ></button>`
        }
      </div>
    </div>
    ${
      count > 0
        ? `<div class="cookie-list" id="cb-cookies-${cat.id}" data-cookie-list="${cat.id}"><div class="cookie-list-inner">${items}</div></div>`
        : ''
    }
  </div>
  `;
};

class CookieBanner extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.state = {
      preferences: { necessary: true, analytics: false, marketing: false, preferences: false },
      modalOpen: false,
    };
    this.l10n = {};
    this.handleStorage = this.handleStorage.bind(this);
    this.handleKey = this.handleKey.bind(this);
    this.handleHashChange = this.handleHashChange.bind(this);
  }

  connectedCallback() {
    try {
      this.l10n = JSON.parse(this.getAttribute('data-l10n') || '{}');
    } catch (_) {
      this.l10n = {};
    }
    this._returnFocusTo = (document.activeElement && document.activeElement !== document.body)
      ? document.activeElement
      : null;
    this.render();
    this.bindEvents();
    this.show();

    const initialView = this.getAttribute('data-initial-view');
    if (initialView === 'preferences' || initialView === 'details') {
      requestAnimationFrame(() => this.openModal());
    } else {
      this.applyHash();
    }

    window.addEventListener('hashchange', this.handleHashChange);
    window.addEventListener('storage', this.handleStorage);
    document.addEventListener('keydown', this.handleKey);
  }

  disconnectedCallback() {
    window.removeEventListener('hashchange', this.handleHashChange);
    window.removeEventListener('storage', this.handleStorage);
    document.removeEventListener('keydown', this.handleKey);
  }

  handleHashChange() {
    this.applyHash();
  }

  handleStorage(event) {
    if (event.key !== STORAGE_KEY || !event.newValue) return;
    if (this._committing || this._dismissing) return;
    this.dismiss();
  }

  handleKey(e) {
    if (e.key !== 'Escape') return;
    if (this.state.modalOpen) {
      e.preventDefault();
      this.closeModal();
    }
  }

  applyHash() {
    const hash = (window.location.hash || '').toLowerCase();
    if (hash === '#cookies' || hash === '#cookie-preferences' || hash === '#cookie-details') {
      this.openModal();
    }
  }

  render() {
    const style = document.createElement('style');
    style.textContent = STYLES;
    this.shadowRoot.appendChild(style);
    const wrapper = document.createElement('div');
    wrapper.innerHTML = TEMPLATE(this.l10n);
    while (wrapper.firstChild) this.shadowRoot.appendChild(wrapper.firstChild);
    this.renderModalList();
  }

  renderModalList() {
    const list = this.shadowRoot.querySelector('[data-modal-list]');
    if (!list) return;
    const cats = CONSENT_CATEGORIES.map((id) => ({
      id,
      required: id === 'necessary',
      checked: this.state.preferences[id],
    }));
    list.innerHTML = cats.map((c) => CATEGORY_ROW(c, this.l10n)).join('');
  }

  bindEvents() {
    this.shadowRoot.addEventListener('click', (e) => {
      const path = (typeof e.composedPath === 'function') ? e.composedPath() : [];
      let actionEl = null;
      let toggleEl = null;
      let expandEl = null;
      let isOverlay = false;
      for (const node of path) {
        if (!node || node.nodeType !== 1) continue;
        if (!actionEl && node.dataset && node.dataset.action) actionEl = node;
        if (!toggleEl && node.dataset && node.dataset.toggle) toggleEl = node;
        if (!expandEl && node.dataset && node.dataset.expand) expandEl = node;
        if (node.dataset && node.dataset.overlay !== undefined) isOverlay = true;
        if (node === this.shadowRoot) break;
      }
      // Click on the overlay backdrop -> close modal (does not commit a choice).
      if (isOverlay && !actionEl && !toggleEl && !expandEl) {
        this.closeModal();
        return;
      }
      const action = actionEl?.dataset.action || e.target.closest('[data-action]')?.dataset.action;
      const toggle = toggleEl?.dataset.toggle || e.target.closest('[data-toggle]')?.dataset.toggle;
      const expand = expandEl?.dataset.expand || e.target.closest('[data-expand]')?.dataset.expand;
      if (expand) {
        this.toggleCookieList(expand, expandEl || e.target.closest('[data-expand]'));
        return;
      }
      if (action === 'accept-all') this.acceptAll();
      else if (action === 'reject-all') this.rejectAll();
      else if (action === 'customize') this.openModal();
      else if (action === 'save') this.savePreferences();
      else if (toggle) this.toggleCategory(toggle);
    });
  }

  toggleCookieList(catId, btn) {
    const panel = this.shadowRoot.querySelector(`[data-cookie-list="${catId}"]`);
    if (!panel) return;
    const isExpanded = btn?.getAttribute('aria-expanded') === 'true';
    if (isExpanded) {
      // Collapse
      const startHeight = panel.scrollHeight;
      panel.style.transition = 'none';
      panel.style.height = `${startHeight}px`;
      void panel.offsetHeight;
      requestAnimationFrame(() => {
        panel.style.transition = 'height 280ms cubic-bezier(0.4, 0, 0.2, 1)';
        panel.style.height = '0px';
      });
      btn.setAttribute('aria-expanded', 'false');
    } else {
      // Expand
      panel.style.transition = 'none';
      panel.style.height = 'auto';
      const targetHeight = panel.scrollHeight;
      panel.style.height = '0px';
      void panel.offsetHeight;
      requestAnimationFrame(() => {
        panel.style.transition = 'height 320ms cubic-bezier(0.22, 1, 0.36, 1)';
        panel.style.height = `${targetHeight}px`;
        const onEnd = () => {
          panel.style.height = 'auto';
          panel.removeEventListener('transitionend', onEnd);
        };
        panel.addEventListener('transitionend', onEnd);
      });
      btn.setAttribute('aria-expanded', 'true');
    }
  }

  show() {
    const banner = this.shadowRoot.querySelector('.banner');
    if (!banner) return;
    requestAnimationFrame(() => {
      banner.style.transition = 'transform 320ms cubic-bezier(0.22, 1, 0.36, 1), opacity 240ms ease';
      banner.style.transform = 'translateY(0)';
      banner.style.opacity = '1';
    });
  }

  openModal() {
    if (this.state.modalOpen) return;
    this.state.modalOpen = true;
    this.renderModalList();
    const overlay = this.shadowRoot.querySelector('[data-overlay]');
    const modal = this.shadowRoot.querySelector('.modal');
    if (overlay) overlay.dataset.visible = 'true';
    if (modal) modal.dataset.visible = 'true';
    setTimeout(() => {
      const firstInteractive = this.shadowRoot.querySelector('.modal [data-toggle], .modal [data-action]');
      if (firstInteractive && typeof firstInteractive.focus === 'function') {
        try { firstInteractive.focus({ preventScroll: true }); } catch (_) { firstInteractive.focus(); }
      }
    }, 80);
  }

  closeModal() {
    if (!this.state.modalOpen) return;
    this.state.modalOpen = false;
    const overlay = this.shadowRoot.querySelector('[data-overlay]');
    const modal = this.shadowRoot.querySelector('.modal');
    if (overlay) overlay.dataset.visible = 'false';
    if (modal) modal.dataset.visible = 'false';
  }

  toggleCategory(id) {
    if (id === 'necessary') return;
    this.state.preferences[id] = !this.state.preferences[id];
    const sw = this.shadowRoot.querySelector(`[data-toggle="${id}"]`);
    if (sw) sw.setAttribute('aria-checked', String(this.state.preferences[id]));
  }

  acceptAll() {
    this.state.preferences = { necessary: true, analytics: true, marketing: true, preferences: true };
    this.commit('accepted');
  }

  rejectAll() {
    this.state.preferences = { necessary: true, analytics: false, marketing: false, preferences: false };
    this.commit('rejected');
  }

  savePreferences() {
    this.commit('custom');
  }

  commit(consentType) {
    if (this._committing) return;
    this._committing = true;

    const prefs = this.state.preferences;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        ...prefs,
        timestamp: Date.now(),
        type: consentType,
      }));
    } catch (_) {}

    this.applyConsent(prefs);
    this.dispatchEvent(new CustomEvent('marmitek:consent-changed', {
      bubbles: true,
      composed: true,
      detail: { ...prefs, type: consentType },
    }));

    this.dismiss();
  }

  applyConsent(prefs) {
    if (window.Shopify?.customerPrivacy?.setTrackingConsent) {
      window.Shopify.customerPrivacy.setTrackingConsent({
        analytics: prefs.analytics,
        marketing: prefs.marketing,
        preferences: prefs.preferences,
        sale_of_data: prefs.marketing,
      }, () => {});
    }

    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        ad_storage: prefs.marketing ? 'granted' : 'denied',
        ad_user_data: prefs.marketing ? 'granted' : 'denied',
        ad_personalization: prefs.marketing ? 'granted' : 'denied',
        analytics_storage: prefs.analytics ? 'granted' : 'denied',
        functionality_storage: prefs.preferences ? 'granted' : 'denied',
        personalization_storage: prefs.preferences ? 'granted' : 'denied',
        security_storage: 'granted',
      });
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'marmitek_consent_update',
      consent_analytics: prefs.analytics,
      consent_marketing: prefs.marketing,
      consent_preferences: prefs.preferences,
    });
  }

  dismiss() {
    if (this._dismissing) return;
    this._dismissing = true;

    const banner = this.shadowRoot.querySelector('.banner');
    const overlay = this.shadowRoot.querySelector('[data-overlay]');
    const modal = this.shadowRoot.querySelector('.modal');

    if (overlay) overlay.dataset.visible = 'false';
    if (modal) modal.dataset.visible = 'false';

    if (banner) {
      banner.style.transition = 'transform 320ms cubic-bezier(0.32, 0.72, 0.24, 1), opacity 240ms cubic-bezier(0.4, 0, 0.2, 1)';
      requestAnimationFrame(() => {
        banner.style.transform = 'translateY(120%)';
        banner.style.opacity = '0';
      });
    }

    const restoreFocus = () => {
      const f = this._returnFocusTo;
      if (f && typeof f.focus === 'function' && document.contains(f)) {
        try { f.focus({ preventScroll: true }); } catch (_) { f.focus(); }
      }
    };

    const cleanup = () => {
      restoreFocus();
      this.remove();
    };
    if (banner) {
      const onEnd = () => {
        banner.removeEventListener('transitionend', onEnd);
        cleanup();
      };
      banner.addEventListener('transitionend', onEnd);
      setTimeout(onEnd, 380);
    } else {
      cleanup();
    }
  }
}

if (!customElements.get('cookie-banner')) {
  customElements.define('cookie-banner', CookieBanner);
}

export { CookieBanner };
